import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, Activity, ChevronDown, ChevronRight, ShieldCheck,
} from "lucide-react";
import { API, SCREENING_STATUS_BADGE } from "./constants";

// ── Screening jobs card ─────────────────────────────────────────────────────
//
// Shows the last N async screening jobs (default: failures only). Each row
// expands into a detail panel with the raw error / phase reached / latency
// / customer info — enough to diagnose "why did this fail" without pod logs.
// Also includes a summary banner with the 100-job rolling failure rate so
// the operator notices upstream-provider degradation early.

function Pill({ label, value }) {
  return (
    <div className="px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded text-center">
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-xs text-slate-200 font-medium mt-0.5 truncate">{String(value)}</p>
    </div>
  );
}

function ScreeningJobRow({ job }) {
  const [open, setOpen] = useState(false);
  const badge = SCREENING_STATUS_BADGE[job.status] || SCREENING_STATUS_BADGE.failed;
  const started = job.started_at ? new Date(job.started_at) : null;
  const startedLabel = started
    ? started.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" })
    : "—";

  return (
    <div
      data-testid={`screening-job-row-${job.job_id}`}
      className="border-b border-slate-800 last:border-0"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-3 hover:bg-slate-900/40 flex items-center gap-3 group"
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${badge.cls}`}
        >
          {badge.label}
        </span>
        <span className="text-sm text-slate-200 truncate flex-1">
          {job.customer_name}
          <span className="ml-2 text-xs text-slate-500">({job.organization_name})</span>
        </span>
        <span className="text-xs font-mono text-slate-400 shrink-0">
          {job.latency_seconds != null ? `${job.latency_seconds}s` : "—"}
        </span>
        <span className="text-[10px] text-slate-500 shrink-0 hidden sm:inline">
          {startedLabel}
        </span>
      </button>

      {open && (
        <div
          data-testid={`screening-job-detail-${job.job_id}`}
          className="px-5 pb-4 -mt-1 bg-slate-950/40 border-t border-slate-800/50"
        >
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs mt-2">
            <div>
              <dt className="text-slate-500 uppercase tracking-wider text-[10px]">Job ID</dt>
              <dd className="font-mono text-slate-300">{job.job_id}</dd>
            </div>
            <div>
              <dt className="text-slate-500 uppercase tracking-wider text-[10px]">Customer ID</dt>
              <dd className="font-mono text-slate-300">{job.customer_id}</dd>
            </div>
            <div>
              <dt className="text-slate-500 uppercase tracking-wider text-[10px]">Fase alcanzada</dt>
              <dd className="text-slate-300">{job.phase || "—"}</dd>
            </div>
            <div>
              <dt className="text-slate-500 uppercase tracking-wider text-[10px]">Intentos</dt>
              <dd className="text-slate-300">{job.attempt || 1}</dd>
            </div>
            <div>
              <dt className="text-slate-500 uppercase tracking-wider text-[10px]">Iniciado</dt>
              <dd className="text-slate-300">{startedLabel}</dd>
            </div>
            <div>
              <dt className="text-slate-500 uppercase tracking-wider text-[10px]">Terminado</dt>
              <dd className="text-slate-300">
                {job.completed_at
                  ? new Date(job.completed_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "medium" })
                  : "—"}
              </dd>
            </div>
          </dl>

          {job.error && (
            <div className="mt-3 p-3 bg-red-500/5 border border-red-500/30 rounded-md">
              <p className="text-[10px] uppercase tracking-wider text-red-400 mb-1">Error</p>
              <pre className="text-xs text-red-200 whitespace-pre-wrap break-words font-mono">
                {job.error}
              </pre>
            </div>
          )}
          {job.last_error && job.status !== "failed" && (
            <div className="mt-3 p-2.5 bg-amber-500/5 border border-amber-500/30 rounded-md">
              <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1">
                Error del primer intento (se reintentó automáticamente)
              </p>
              <pre className="text-xs text-amber-200 whitespace-pre-wrap break-words font-mono">
                {job.last_error}
              </pre>
            </div>
          )}
          {job.status === "done" && job.result && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <Pill label="Coincidencias" value={job.result.total_matches ?? 0} />
              <Pill label="Riesgo" value={job.result.risk_classification ?? "—"} />
              <Pill label="Confianza" value={job.result.max_confidence ?? "—"} />
              <Pill label="Listas" value={(job.result.lists_screened || []).length} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ScreeningJobsCard() {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("failed"); // "failed" | "all"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [jobsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/superadmin/screening-jobs?limit=30${filter !== "all" ? `&status=${filter}` : ""}`,
          { withCredentials: true }),
        axios.get(`${API}/superadmin/screening-jobs/summary`, { withCredentials: true }),
      ]);
      setJobs(jobsRes.data?.items || []);
      setSummary(summaryRes.data || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
    // 60s — failures don't change second-by-second, no point hammering.
    const interval = setInterval(load, 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  const healthCls = summary?.health === "fail"
    ? "bg-red-500/10 text-red-300 border-red-500/30"
    : summary?.health === "warn"
      ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
      : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";

  return (
    <div
      data-testid="screening-jobs-card"
      className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
    >
      <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-rose-500/10 rounded-lg shrink-0">
            <ShieldCheck className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Screenings recientes</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-[600px]">
              Últimos jobs de screening PEP/OFAC en todo el sistema. Despliega
              cualquier fila para ver el error completo, fase alcanzada, intentos
              y latencia.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {summary && (
            <span
              data-testid="screening-health-badge"
              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[11px] font-medium ${healthCls}`}
              title="Tasa de fallo de los últimos 100 jobs"
            >
              <Activity className="w-3 h-3" />
              {summary.failed}/{summary.window} fallidos ({summary.failure_rate_pct}%)
            </span>
          )}
          <div className="flex rounded-md overflow-hidden border border-slate-700">
            {[
              { key: "failed", label: "Solo fallidos" },
              { key: "all", label: "Todos" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setFilter(opt.key)}
                data-testid={`screening-filter-${opt.key}`}
                className={`px-3 py-1 text-xs transition-colors ${
                  filter === opt.key
                    ? "bg-rose-500/20 text-rose-300"
                    : "bg-slate-900 text-slate-400 hover:bg-slate-800"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-500/10 border-b border-red-500/30 px-5 py-3">
          {error}
        </div>
      )}

      {loading && jobs.length === 0 ? (
        <div className="h-24 flex items-center justify-center text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" />
          Cargando jobs…
        </div>
      ) : jobs.length === 0 ? (
        <div
          data-testid="screening-empty"
          className="h-24 flex items-center justify-center text-slate-500 text-sm"
        >
          {filter === "failed" ? "Sin screenings fallidos recientes" : "Sin screenings registrados"}
        </div>
      ) : (
        <div>{jobs.map((j) => <ScreeningJobRow key={j.job_id} job={j} />)}</div>
      )}
    </div>
  );
}
